// unmi theme: global behaviour. Vanilla ES module, no dependencies.

const $ = (s, r = document) => r.querySelector(s);
const $$ = (s, r = document) => Array.from(r.querySelectorAll(s));

// ---- loading screen: plays once per visit, fills its line as the page loads, then lifts ----
{
  const html = document.documentElement;
  const loader = $('[data-loader]');
  let seen = false;
  try { seen = sessionStorage.getItem('unmi-loader') === '1'; } catch { /* private mode */ }
  const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (!loader || seen || reduce) {
    html.classList.add('loader-seen', 'is-loaded');
    if (loader) loader.remove(); // nothing left behind to hold the page still
  } else {
    const t0 = performance.now();
    const paws = [...loader.querySelectorAll('.loader__paw')];
    const set = (v) => paws.forEach((p, i) => p.classList.toggle('is-on', v >= (i + 1) / paws.length - 0.001));
    set(0);
    let loaded = document.readyState === 'complete';
    addEventListener('load', () => { loaded = true; }, { once: true });
    const MIN = 3000, MAX = 5000; // long enough for the wordmark to draw in; never longer than 5 s
    const START = 1000; // the paws fade in at 0.9 s: they start empty, then light one by one
    const tick = () => {
      const t = performance.now() - t0;
      set(Math.min(loaded ? 1 : 0.8, Math.max(0, (t - START) / (MIN - START))));
      if ((loaded && t >= MIN) || t >= MAX) {
        set(1);
        setTimeout(() => {
          loader.classList.add('is-done');
          html.classList.add('is-loaded');
          try { sessionStorage.setItem('unmi-loader', '1'); } catch { /* private mode */ }
          setTimeout(() => loader.remove(), 1000);
        }, 350);
      } else requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
    loader.addEventListener('click', () => { loaded = true; }); // a tap lets impatient visitors through sooner
  }
}

// ---- mobile menu ----
const toggle = $('[data-menu-toggle]');
const mobileNav = $('[data-mobile-nav]');
if (toggle && mobileNav) {
  toggle.addEventListener('click', () => {
    const open = toggle.getAttribute('aria-expanded') === 'true';
    toggle.setAttribute('aria-expanded', String(!open));
    mobileNav.hidden = open;
    if (!open) mobileNav.querySelector('a')?.focus();
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && toggle.getAttribute('aria-expanded') === 'true') {
      toggle.setAttribute('aria-expanded', 'false');
      mobileNav.hidden = true;
      toggle.focus();
    }
  });
}

// ---- one quiet reveal, for the step list only ----
const reveals = $$('.reveal');
if (reveals.length && 'IntersectionObserver' in window) {
  const io = new IntersectionObserver((entries) => {
    for (const e of entries) if (e.isIntersecting) { e.target.classList.add('is-in'); io.unobserve(e.target); }
  }, { rootMargin: '0px 0px -10% 0px' });
  reveals.forEach((el, i) => { el.style.transitionDelay = `${i * 90}ms`; io.observe(el); });
} else reveals.forEach((el) => el.classList.add('is-in'));

// ---- sticky action for phones: appears once the page's own button has scrolled away ----
const sticky = $('[data-sticky-action]');
const anchor = $('[data-hero-cta]') || $('[data-product-submit]');
if (sticky && anchor) {
  const end = $('[data-sticky-hide]');
  let raf = 0;
  const update = () => {
    raf = 0;
    const gone = anchor.getBoundingClientRect().bottom < 0;
    const atEnd = end ? end.getBoundingClientRect().top < window.innerHeight : false;
    sticky.classList.toggle('is-visible', gone && !atEnd);
  };
  addEventListener('scroll', () => { if (!raf) raf = requestAnimationFrame(update); }, { passive: true });
  update();
}

// ---- the diary's photographs develop as they come into view ----
const calm = matchMedia('(prefers-reduced-motion: reduce)').matches;
const developing = $$('[data-develop]');
if (developing.length) {
  if (!calm && 'IntersectionObserver' in window) {
    const io = new IntersectionObserver((entries) => {
      for (const e of entries) if (e.isIntersecting) { e.target.classList.add('is-developed'); io.unobserve(e.target); }
    }, { threshold: 0.2 });
    developing.forEach((el) => io.observe(el));
  } else developing.forEach((el) => el.classList.add('is-developed'));
}

// ---- images that fade in as they arrive ----
const fading = $$('.fade-in');
if (fading.length) {
  if (!calm && 'IntersectionObserver' in window) {
    const io = new IntersectionObserver((entries) => {
      for (const e of entries) if (e.isIntersecting) { e.target.classList.add('is-in'); io.unobserve(e.target); }
    }, { threshold: 0.15 });
    fading.forEach((el) => io.observe(el));
  } else fading.forEach((el) => el.classList.add('is-in'));
}

// ---- the diary's paws: each lights its own toe once its step is scrolled past, and stays lit ----
const paws = $$('[data-diary-paw]');
if (paws.length) {
  const light = (p) => p.querySelectorAll('.diary-paw__part--target').forEach((t) => t.classList.add('is-lit'));
  if ('IntersectionObserver' in window) {
    const io = new IntersectionObserver((entries) => {
      for (const e of entries) if (e.isIntersecting) { light(e.target); io.unobserve(e.target); }
    }, { rootMargin: '0px 0px -30% 0px', threshold: 0.5 });
    paws.forEach((p) => io.observe(p));
  } else paws.forEach(light);
}

// ---- a trail of paw prints from paw to paw, walking through the space between steps; prints light as the reader passes ----
const trailHost = $('[data-diary-trail]');
if (trailHost && trailHost.querySelectorAll('[data-diary-paw]').length > 1) {
  const NS = 'http://www.w3.org/2000/svg';
  const svg = document.createElementNS(NS, 'svg');
  svg.setAttribute('class', 'diary-trail'); svg.setAttribute('aria-hidden', 'true');
  trailHost.prepend(svg);
  let treats = [];
  const build = () => {
    svg.replaceChildren(); treats = [];
    const box = trailHost.getBoundingClientRect();
    const pts = [...trailHost.querySelectorAll('[data-diary-paw]')].map((p) => {
      const r = p.getBoundingClientRect();
      return { x: r.left + r.width / 2 - box.left, top: r.top - box.top, bottom: r.bottom - box.top };
    });
    svg.setAttribute('viewBox', `0 0 ${box.width} ${box.height}`);
    // walk down from one paw, across the empty gap between the two steps, then down into the next paw
    const rows = [...trailHost.children].filter((el) => el.matches('.diary__entry')).map((el) => { const r = el.getBoundingClientRect(); return { top: r.top - box.top, bottom: r.bottom - box.top }; });
    const stepAt = (y) => rows.findIndex((r) => y >= r.top && y <= r.bottom);
    for (let i = 0; i < pts.length - 1; i++) {
      const a = pts[i], b = pts[i + 1];
      const ra = rows[stepAt(a.top)], rb = rows[stepAt(b.top)];
      const gap = ra && rb ? (ra.bottom + rb.top) / 2 : (a.bottom + b.top) / 2;
      const y0 = a.bottom + 18, y1 = b.top - 18;
      // one smooth S: leave this paw heading down, cross in the middle of the empty gap, arrive at the next paw from above.
      // The handles are pushed toward the gap until the whole curve keeps its distance from every photograph.
      const photos = [...trailHost.querySelectorAll('.diary__photo .mount__frame, .diary__photo figcaption')].map((m) => { const r = m.getBoundingClientRect(); return { l: r.left - box.left, t: r.top - box.top, r: r.right - box.left, b: r.bottom - box.top }; });
      const CLEAR = 52;
      const curve = (k) => `M${a.x} ${y0} C${a.x} ${y0 + (gap - y0) * k} ${b.x} ${y1 - (y1 - gap) * k} ${b.x} ${y1}`;
      const tooClose = (probe) => {
        const L = probe.getTotalLength();
        for (let t = 0; t <= L; t += 12) {
          const q = probe.getPointAtLength(t);
          if (photos.some((ph) => q.x > ph.l - CLEAR && q.x < ph.r + CLEAR && q.y > ph.t - CLEAR && q.y < ph.b + CLEAR)) return true;
        }
        return false;
      };
      const path = document.createElementNS(NS, 'path');
      svg.append(path);
      let k = 1.1;
      path.setAttribute('d', curve(k));
      while (k < 2.2 && tooClose(path)) { k += 0.15; path.setAttribute('d', curve(k)); }
      const len = path.getTotalLength();
      path.remove();
      const n = Math.max(3, Math.round(len / 46));
      for (let k = 1; k < n; k++) {
        const at = (len * k) / n, p0 = path.getPointAtLength(at), p1 = path.getPointAtLength(Math.min(len, at + 1));
        const theta = Math.atan2(p1.y - p0.y, p1.x - p0.x);
        const side = k % 2 ? 3.5 : -3.5; // a gentle left, right: a walking gait on a smooth line
        const x = p0.x - Math.sin(theta) * side, y = p0.y + Math.cos(theta) * side;
        const g = document.createElementNS(NS, 'g');
        g.setAttribute('class', 'diary-trail__treat');
        g.setAttribute('transform', `translate(${x.toFixed(1)} ${y.toFixed(1)}) rotate(${((theta * 180) / Math.PI + 90).toFixed(1)}) scale(1.4)`);
        g.innerHTML = '<ellipse cx="0" cy="2.6" rx="4.2" ry="3.5"/><circle cx="-4.4" cy="-2.4" r="1.7"/><circle cx="-1.5" cy="-4.6" r="1.8"/><circle cx="1.5" cy="-4.6" r="1.8"/><circle cx="4.4" cy="-2.4" r="1.7"/>';
        svg.append(g); treats.push({ g, y: p0.y });
      }
    }
    update();
  };
  const update = () => {
    const line = innerHeight * 0.62 - trailHost.getBoundingClientRect().top;
    for (const t of treats) t.g.classList.toggle('is-lit', calm || t.y < line);
  };
  let raf = 0;
  addEventListener('scroll', () => { if (!raf) raf = requestAnimationFrame(() => { raf = 0; update(); }); }, { passive: true });
  if ('ResizeObserver' in window) new ResizeObserver(() => build()).observe(trailHost); else addEventListener('resize', build);
  build();
}

// ---- a clipped stack of photos fans out as it arrives ----
$$('[data-photo-fan]').forEach((fan) => {
  if (calm || !('IntersectionObserver' in window)) return fan.classList.add('is-open');
  const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) { fan.classList.add('is-open'); io.disconnect(); } }, { threshold: 0.35 });
  io.observe(fan);
});

// ---- the whiskers beside the slogan light up when they come into view ----
$$('.whiskers').forEach((w) => {
  if (!('IntersectionObserver' in window)) return w.classList.add('is-lit');
  const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) { w.classList.add('is-lit'); io.disconnect(); } }, { rootMargin: '0px 0px -20% 0px', threshold: 1 });
  io.observe(w);
});

// ---- the UNMI collection: the cards sit around a drum that bulges toward the reader and turns as the page scrolls, or by its paws ----
{
  const host = $('[data-sellers]');
  const ring = host && host.querySelector('[data-sellers-ring]');
  if (ring && !calm && 'IntersectionObserver' in window) {
    const cards = [...ring.children];
    const n = cards.length;
    const firsts = cards.filter((c) => !c.hasAttribute('aria-hidden')).length;
    let raf = 0, near = false, shift = 0;
    host.classList.add('is-turning');
    // cards turned by the scroll alone: 0 → 1 while the section passes, the first product facing the reader mid-pass
    const base = () => { const box = host.getBoundingClientRect(); return ((innerHeight - box.top) / (innerHeight + box.height) - 0.5) * 4; };
    const place = () => {
      raf = 0;
      const w = cards[0].offsetWidth || 1;
      const R = w * 2.8; // radius of the drum
      const step = (w * 1.08) / R; // angle between cards
      host.style.setProperty('--persp', Math.round(w * 3.6) + 'px');
      const t = base() + shift;
      cards.forEach((c, i) => {
        let k = (((i - t) % n) + n) % n;
        if (k > n / 2) k -= n;
        const a = k * step;
        const fade = Math.max(0, Math.min(1, (1.42 - Math.abs(a)) / 0.35));
        // convex: the front card is nearest; either side the cards curve away and turn their faces outward
        c.style.transform = `translate3d(${(R * Math.sin(a)).toFixed(1)}px, 0, ${(R * (Math.cos(a) - 1)).toFixed(1)}px) rotateY(${a.toFixed(4)}rad)`;
        c.style.opacity = fade.toFixed(3);
        c.style.visibility = fade > 0 ? 'visible' : 'hidden';
      });
    };
    const ask = () => { if (near && !raf) raf = requestAnimationFrame(place); };
    new IntersectionObserver(([e]) => { near = e.isIntersecting; ask(); }, { rootMargin: '25% 0px' }).observe(host);
    addEventListener('scroll', ask, { passive: true });
    addEventListener('resize', () => { near = true; ask(); }, { passive: true });
    // a card reached by keyboard turns to face the reader
    const settle = () => { host.classList.add('is-settling'); clearTimeout(settle.t); settle.t = setTimeout(() => host.classList.remove('is-settling'), 650); };
    ring.addEventListener('focusin', (e) => {
      const i = cards.indexOf(e.target.closest('[data-seller]'));
      if (i < 0 || i >= firsts) return;
      shift = i - base(); settle(); place();
    });
    ring.addEventListener('focusout', (e) => { if (!ring.contains(e.relatedTarget)) { shift = 0; settle(); place(); } });
    // the paws: one piece left or right, from whichever card is nearest the front
    const nav = host.querySelector('[data-sellers-nav]');
    if (nav) {
      nav.hidden = false;
      nav.addEventListener('click', (e) => {
        const paw = e.target.closest('[data-sellers-step]');
        if (!paw) return;
        const b = base();
        shift = Math.round(b + shift) + Number(paw.dataset.sellersStep) - b;
        settle(); place();
      });
    }
    // drag or swipe sideways to turn the drum (vertical swipes still scroll the page), then rest on the nearest card
    const stage = host.querySelector('[data-sellers-stage]') || ring;
    let drag = null, dragged = false;
    host.classList.add('is-draggable');
    stage.addEventListener('pointerdown', (e) => {
      if (e.button > 0) return;
      drag = { id: e.pointerId, x: e.clientX, y: e.clientY, from: shift, on: false };
      dragged = false;
    });
    stage.addEventListener('pointermove', (e) => {
      if (!drag || e.pointerId !== drag.id) return;
      const dx = e.clientX - drag.x;
      if (!drag.on) {
        if (Math.abs(dx) < 8 || Math.abs(dx) < Math.abs(e.clientY - drag.y)) return;
        drag.on = dragged = true;
        try { stage.setPointerCapture(e.pointerId); } catch {}
        host.classList.add('is-dragging');
      }
      shift = drag.from - dx / ((cards[0].offsetWidth || 1) * 1.08);
      place();
    });
    const release = (e) => {
      if (!drag || e.pointerId !== drag.id) return;
      if (drag.on) {
        host.classList.remove('is-dragging');
        const b = base();
        shift = Math.round(b + shift) - b;
        settle(); place();
      }
      drag = null;
    };
    stage.addEventListener('pointerup', release);
    stage.addEventListener('pointercancel', release);
    // a drag is not a click on the card it ended over
    stage.addEventListener('click', (e) => { if (dragged) { e.preventDefault(); e.stopPropagation(); dragged = false; } }, true);
    stage.addEventListener('dragstart', (e) => e.preventDefault());
    place();
  }
}

// ---- process diary: the guide marks the step being read, and the ones before it as done ----
{
  const host = $('[data-process]');
  const links = host ? [...host.querySelectorAll('[data-process-link]')] : [];
  if (links.length && 'IntersectionObserver' in window) {
    const stages = [...host.querySelectorAll('[data-process-stage]')];
    const mark = (id) => {
      const at = links.findIndex((l) => l.dataset.processLink === id);
      links.forEach((l, i) => {
        l.classList.toggle('is-done', i < at);
        if (i === at) l.setAttribute('aria-current', 'step'); else l.removeAttribute('aria-current');
      });
    };
    const io = new IntersectionObserver((entries) => {
      for (const e of entries) if (e.isIntersecting) mark(e.target.id);
    }, { rootMargin: '-45% 0px -45% 0px' });
    stages.forEach((s) => io.observe(s));
  }
}

// ---- collection cards with several photos: a slow crossfade while in view (the first photo only, for reduced motion) ----
{
  const fades = $$('[data-shelf-fade]');
  if (fades.length && !calm && 'IntersectionObserver' in window) {
    fades.forEach((box, n) => {
      const imgs = [...box.querySelectorAll('img')];
      let i = 0, timer = 0;
      const next = () => { imgs[i].classList.remove('is-shown'); i = (i + 1) % imgs.length; imgs[i].loading = 'eager'; imgs[i].classList.add('is-shown'); };
      new IntersectionObserver(([e]) => {
        clearInterval(timer);
        if (e.isIntersecting) timer = setInterval(next, 3200 + n * 400);
      }, { threshold: 0.4 }).observe(box);
    });
  }
}

// ---- a mounted video plays only where motion is welcome; otherwise the photograph stays ----
const saveData = navigator.connection && navigator.connection.saveData;
if (!calm && !saveData) {
  $$('[data-motion-video]').forEach((v) => {
    v.querySelectorAll('source[data-src]').forEach((s) => { s.src = s.dataset.src; });
    v.addEventListener('playing', () => v.classList.add('is-playing'), { once: true });
    v.load();
    v.play().catch(() => {});
  });
}

// ---- sideways strips (the gallery on phones) take keyboard focus only while they scroll ----
const strips = $$('[data-scroll-strip]');
if (strips.length) {
  const sync = () => strips.forEach((el) => {
    if (el.scrollWidth > el.clientWidth + 1) el.setAttribute('tabindex', '0'); else el.removeAttribute('tabindex');
  });
  sync();
  addEventListener('resize', sync, { passive: true });
}

// ---- feature modules, loaded only where used ----
// the homepage sculpture photograph: sways left and right, and turns when dragged
{
  const stage = $('[data-turn]');
  const card = stage && stage.querySelector('[data-turn-card]');
  if (card) {
    const MAX = 38; // degrees: beyond this a flat photograph gives itself away
    let angle = 0, drag = null, released = -1e9, raf = 0;
    const set = (a, float) => {
      card.style.setProperty('--turn', a.toFixed(2) + 'deg');
      card.style.setProperty('--float', float.toFixed(2) + 'px');
      stage.style.setProperty('--shadow', (0.6 + 0.4 * Math.cos((a * Math.PI) / 180)).toFixed(3));
    };
    const tick = (now) => {
      raf = 0;
      if (!drag) {
        const idle = Math.min(1, Math.max(0, (now - released - 1200) / 1500));
        const sway = calm ? 0 : Math.sin(now / 1000 * 0.55) * 24;
        angle += (sway - angle) * 0.05 * idle;
      }
      set(angle, calm ? 0 : Math.sin(now / 1000 * 0.9) * 6);
      if (!document.hidden) raf = requestAnimationFrame(tick);
    };
    stage.addEventListener('pointerdown', (e) => { drag = { x: e.clientX, a: angle }; stage.setPointerCapture(e.pointerId); stage.classList.add('is-dragging'); });
    stage.addEventListener('pointermove', (e) => { if (drag) angle = Math.max(-MAX, Math.min(MAX, drag.a + (e.clientX - drag.x) * 0.25)); });
    const end = () => { if (drag) { drag = null; released = performance.now(); stage.classList.remove('is-dragging'); } };
    stage.addEventListener('pointerup', end); stage.addEventListener('pointercancel', end);
    document.addEventListener('visibilitychange', () => { if (!document.hidden && !raf) raf = requestAnimationFrame(tick); });
    raf = requestAnimationFrame(tick);
  }
}
if ($('[data-true-scale]')) import(new URL('true-scale.js', import.meta.url).href);
if ($('[data-product-form]')) import(new URL('product.js', import.meta.url).href);
if ($('[data-cart-drawer]') || $('[data-cart-page]')) import(new URL('cart.js', import.meta.url).href);
